import { Flex, Group } from '@mantine/core';
import { useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import CashflowContent from 'src/cashflows/CashflowContent';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { useContainerQuery } from 'src/UI/app-container/ContainerBreakpoint';
import { useContentMeta } from 'src/UI/app-container';
import { useAppContainerContext } from 'src/UI/app-container/AppContainerContext';
import { ChatMode } from 'src/UI/app-container/chat/types';
import FileUploadPage, {
  FileUploadTabNames,
} from 'src/data-capture/file-management/FileUploadPage';
import { FinancialContent } from 'src/financial/financial-data/FinancialContent';
import NonFinancialDataContent from 'src/non-financial-data/NonFanancialDataContent';
import { BackToClientsButton } from 'src/non-financial-data/v2/BackToClientsButton';
import { FamilyIndividualToggle } from 'src/non-financial-data/v2/FamilyIndividualToggle';
import { useFamilyMembers } from 'src/non-financial-data/v2/family/useFamilyMembers';
import UnderlineTabs from 'src/UI/page-nav/UnderlineTabs';
import ClientMeetingsContent from 'src/meetings/client/ClientMeetingsContent';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { NameFormat, formatName } from 'src/utils/nameFormatting';

export const ClientTabs = {
  NonFinancialData: 'non-financial-data',
  FinancialData: 'financial-data',
  Cashflows: 'cashflows',
  Documents: 'document',
  Meetings: 'meetings',
} as const;
type TabName = (typeof ClientTabs)[keyof typeof ClientTabs];

export const CLIENT_TABS_REGEX = new RegExp(
  `/client/[^/]+/(${Object.values(ClientTabs).join('|')}).*`,
);

export default function ClientData() {
  const { clientId, client } = useCurrentClient();
  const { tab, sectionSlug } = useParams<{ tab: TabName; sectionSlug?: string }>();
  const { flagEnabledIndividualDetailsRework, flagEnabledFamilyScreenRework } = useFlags();
  const { setContentPaneConfig } = useAppContainerContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  useContentMeta({ pageType: 'client', entityId: clientId, chatMode: ChatMode.CLIENT });

  const fullName = formatName(client?.firstName, client?.lastName, NameFormat.NATURAL);
  const familyName = client?.family?.name;
  const individualLabel = fullName || 'Individual';
  const familyLabel = familyName || 'Family';

  useEffect(() => {
    if (!flagEnabledIndividualDetailsRework) return;
    setContentPaneConfig({ p: 'xxl', bg: 'white' });
    return () => setContentPaneConfig({});
  }, [flagEnabledIndividualDetailsRework, setContentPaneConfig]);

  const tabContent = getClientTabContent(tab);
  const cq = useContainerQuery();

  const hideChrome = tab === ClientTabs.FinancialData && sectionSlug === 'create';

  const isProfileTab = tab === ClientTabs.NonFinancialData;
  const isFamilyToggleFunctional =
    flagEnabledIndividualDetailsRework && flagEnabledFamilyScreenRework && isProfileTab;
  const viewParam = searchParams.get('view');
  const toggleValue = isFamilyToggleFunctional && viewParam === 'family' ? 'family' : 'individual';

  const { members, hasOtherMembers } = useFamilyMembers({
    enabled: isFamilyToggleFunctional && !!client?.familyId,
  });
  const showMemberDropdown = isFamilyToggleFunctional && hasOtherMembers;

  function handleToggleChange(value: 'family' | 'individual') {
    if (value === 'family') {
      if (client?.familyId) {
        navigate(`/family/${client.familyId}/profile?member=${clientId}`);
      } else {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev);
          next.set('view', 'family');
          return next;
        });
      }
    } else {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete('view');
        return next;
      });
    }
  }

  function handleMemberSelect(memberId: string) {
    navigate(`/client/${memberId}/${ClientTabs.NonFinancialData}`);
  }

  const toggle = isFamilyToggleFunctional ? (
    <FamilyIndividualToggle
      variant="profile"
      familyScreenEnabled
      value={toggleValue}
      onChange={handleToggleChange}
      familyLabel={familyLabel}
      individualLabel={individualLabel}
      members={showMemberDropdown ? members : undefined}
      onMemberSelect={showMemberDropdown ? handleMemberSelect : undefined}
    />
  ) : (
    <FamilyIndividualToggle
      variant="profile"
      value="individual"
      onChange={() => {}}
      familyLabel={familyLabel}
      individualLabel={individualLabel}
      familyTooltip="Work in progress"
    />
  );

  return (
    <Flex
      direction="column"
      gap={flagEnabledIndividualDetailsRework ? 'lg' : cq({ base: 'sm', xl: 'md' })}
      pos={'relative'}
    >
      {!hideChrome && (
        <UnderlineTabs
          mode="router"
          routerTabsV2={flagEnabledIndividualDetailsRework}
          showClientSearch={!flagEnabledIndividualDetailsRework}
          leadingContent={
            flagEnabledIndividualDetailsRework ? (
              <Group gap="sm" wrap="nowrap">
                <BackToClientsButton />
                {toggle}
              </Group>
            ) : undefined
          }
          integrateBreadcrumb
          tabs={[
            {
              label: 'Profile',
              href: `/client/${clientId}/${ClientTabs.NonFinancialData}`,
            },
            {
              label: 'Financial',
              href: `/client/${clientId}/${ClientTabs.FinancialData}`,
            },
            {
              label: 'Cashflows',
              href: `/client/${clientId}/${ClientTabs.Cashflows}`,
            },
            {
              label: 'Documents',
              href: `/client/${clientId}/${ClientTabs.Documents}/${FileUploadTabNames.ToReview}`,
              matchHref: `/client/${clientId}/${ClientTabs.Documents}`,
            },
            {
              label: 'Meetings',
              href: `/client/${clientId}/${ClientTabs.Meetings}`,
            },
          ]}
        />
      )}

      {tabContent}
    </Flex>
  );
}

function getClientTabContent(tab: TabName | undefined) {
  if (tab === ClientTabs.NonFinancialData) {
    return <NonFinancialDataContent />;
  }
  if (tab === ClientTabs.FinancialData) {
    return <FinancialContent />;
  }
  if (tab === ClientTabs.Cashflows) {
    return <CashflowContent />;
  }
  if (tab === ClientTabs.Documents) {
    return <FileUploadPage />;
  }
  if (tab === ClientTabs.Meetings) {
    return <ClientMeetingsContent />;
  }
  return null;
}
